// src/cockpit4/components/ContentABExperimentPanel.tsx
// ============================================
// P1+ A/B 内容实验：对比不同内容版本在 AI 中的可见性效果
//   实验 = 问题 + 变体A + 变体B + 各自结果(提及/首条/前3) + 结论
//   存储：localStorage xeaos_ab_experiments
// ============================================

import { useState } from "react";
import { FlaskConical, Plus, Trash2, Copy } from "lucide-react";

interface ABVariant { text: string; mentioned: boolean; firstPosition: boolean; top3: boolean; }
interface Experiment {
  id: string;
  name: string;
  question: string;
  platform: string;
  variantA: ABVariant;
  variantB: ABVariant;
  conclusion: string;
  time: string;
}

const KEY = "xeaos_ab_experiments";
const load = (): Experiment[] => {
  try { const r = localStorage.getItem(KEY); if (r) return JSON.parse(r); } catch { /* ignore */ }
  return [];
};

const PLATFORMS = ["豆包", "文心", "通义", "DeepSeek"];

export default function ContentABExperimentPanel() {
  const [exps, setExps] = useState<Experiment[]>(load);
  const [name, setName] = useState("");
  const [question, setQuestion] = useState("");
  const [platform, setPlatform] = useState("豆包");

  const save = (list: Experiment[]) => { setExps(list); try { localStorage.setItem(KEY, JSON.stringify(list)); } catch { /* ignore */ } };

  const add = () => {
    if (!question.trim()) return;
    const e: Experiment = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      name: name.trim() || `实验 ${exps.length + 1}`,
      question: question.trim(),
      platform,
      variantA: { text: "", mentioned: false, firstPosition: false, top3: false },
      variantB: { text: "", mentioned: false, firstPosition: false, top3: false },
      conclusion: "",
      time: new Date().toISOString().slice(0, 16).replace("T", " "),
    };
    save([e, ...exps]);
    setName(""); setQuestion("");
  };

  const update = (id: string, v: "A" | "B", field: keyof ABVariant, val: any) => {
    save(exps.map((e) => e.id === id ? { ...e, [`variant${v}`]: { ...e[`variant${v}` as "variantA" | "variantB"], [field]: val } } : e));
  };
  const updateConclusion = (id: string, val: string) => save(exps.map((e) => e.id === id ? { ...e, conclusion: val } : e));
  const del = (id: string) => save(exps.filter((e) => e.id !== id));

  const score = (v: ABVariant) => (v.mentioned ? 40 : 0) + (v.firstPosition ? 35 : 0) + (v.top3 ? 25 : 0);

  return (
    <div className="space-y-2">
      <div className="text-[10px] font-mono text-gray-400 flex items-center gap-1.5">
        <FlaskConical size={11} className="text-purple-400" />
        A/B 内容实验：同一问题用两种内容版本测 AI 可见性差异（得分=40提及+35首条+25前3）
      </div>

      <div className="flex items-center gap-1.5 flex-wrap">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="实验名（选填）"
          className="px-2 py-1.5 rounded bg-white/[0.04] border border-white/[0.08] text-[10px] text-gray-300 outline-none focus:border-purple-500/40" />
        <select value={platform} onChange={(e) => setPlatform(e.target.value)}
          className="px-2 py-1.5 rounded bg-white/[0.04] border border-white/[0.08] text-[10px] text-gray-300 outline-none">
          {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <input value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="测试问题（如：XEAOS 怎么样？）"
          className="flex-1 min-w-[180px] px-2 py-1.5 rounded bg-white/[0.04] border border-white/[0.08] text-[10px] text-gray-300 outline-none focus:border-purple-500/40" />
        <button onClick={add} className="flex items-center gap-1 px-2.5 py-1.5 rounded text-[10px] font-mono text-white bg-purple-600/80 hover:bg-purple-600 transition">
          <Plus size={10} /> 新建实验
        </button>
      </div>

      <div className="space-y-2 max-h-[340px] overflow-y-auto">
        {exps.length === 0 && <p className="text-[10px] text-gray-600 text-center py-3">暂无实验。新建后，把两个内容版本分别拿去问 AI，把结果勾回来对比。</p>}
        {exps.map((e) => {
          const sa = score(e.variantA); const sb = score(e.variantB);
          const winner = sa === sb ? "平" : sa > sb ? "A" : "B";
          return (
            <div key={e.id} className="rounded border border-white/[0.05] bg-white/[0.02] p-2">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-[10px] font-bold text-white">{e.name}</span>
                <span className="text-[8px] font-mono text-purple-300">{e.platform}</span>
                <span className="text-[9px] text-gray-400 truncate flex-1">{e.question}</span>
                <span className={`text-[9px] font-mono px-1 rounded ${winner === "A" ? "bg-blue-500/15 text-blue-300" : winner === "B" ? "bg-emerald-500/15 text-emerald-300" : "bg-gray-500/15 text-gray-400"}`}>胜出：{winner}</span>
                <button onClick={() => del(e.id)} className="text-gray-600 hover:text-red-400"><Trash2 size={9} /></button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {(["A", "B"] as const).map((v) => {
                  const vr = v === "A" ? e.variantA : e.variantB;
                  const sc = score(vr);
                  return (
                    <div key={v} className="rounded border border-white/[0.04] bg-white/[0.01] p-1.5">
                      <div className="flex items-center gap-1.5 mb-1">
                        <span className={`text-[9px] font-mono font-bold ${v === "A" ? "text-blue-400" : "text-emerald-400"}`}>变体{v}</span>
                        <span className="text-[8px] text-gray-600">得分 {sc}</span>
                      </div>
                      <textarea value={vr.text} onChange={(ev) => update(e.id, v, "text", ev.target.value)} rows={2} placeholder="内容版本…"
                        className="w-full px-1.5 py-1 rounded bg-white/[0.04] border border-white/[0.06] text-[9px] text-gray-300 outline-none resize-none" />
                      <div className="flex items-center gap-2 mt-1 text-[8px] font-mono">
                        <label className="flex items-center gap-0.5 text-gray-400"><input type="checkbox" checked={vr.mentioned} onChange={(ev) => update(e.id, v, "mentioned", ev.target.checked)} /> 提及</label>
                        <label className="flex items-center gap-0.5 text-gray-400"><input type="checkbox" checked={vr.firstPosition} onChange={(ev) => update(e.id, v, "firstPosition", ev.target.checked)} /> 首条</label>
                        <label className="flex items-center gap-0.5 text-gray-400"><input type="checkbox" checked={vr.top3} onChange={(ev) => update(e.id, v, "top3", ev.target.checked)} /> 前3</label>
                      </div>
                    </div>
                  );
                })}
              </div>
              <input value={e.conclusion} onChange={(ev) => updateConclusion(e.id, ev.target.value)} placeholder="结论：哪个版本为什么更好？"
                className="w-full mt-1.5 px-1.5 py-1 rounded bg-white/[0.04] border border-white/[0.06] text-[9px] text-gray-400 outline-none" />
            </div>
          );
        })}
      </div>
    </div>
  );
}
